import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  AppBar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { useAuth } from "@mb-pro-ui/utils";
import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation } from "wouter";
import { useLocale } from "../locales/LocaleProvider";
import { orgLabel, useActiveOrg } from "../org/OrgContext";

const languageNames: Record<string, string> = {
  hu: "Magyar",
  en: "English",
  ro: "Română",
  sk: "Slovenčina",
};

interface TopBarProps {
  title: ReactNode;
  // App-absolute path of the parent page; no back arrow when omitted.
  back?: string;
  end?: ReactNode;
}

export default function TopBar({ title, back, end }: TopBarProps) {
  const intl = useIntl();
  const org = useActiveOrg();
  const { signout } = useAuth();
  const { locale, locales, setLocale } = useLocale();
  const [, navigate] = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const multiOrg = org.choices.length > 1;
  const unnamed = intl.formatMessage({ defaultMessage: "Main organization" });

  return (
    <AppBar position="sticky" elevation={1}>
      <Toolbar variant="dense" disableGutters sx={{ minHeight: 48, px: 0.5, gap: 0.5 }}>
        {back && (
          <IconButton
            color="inherit"
            component={Link}
            href={back}
            aria-label={intl.formatMessage({ defaultMessage: "Back" })}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0, pl: back ? 0 : 1 }}>
          <Typography variant="subtitle2" noWrap component="div" sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          {multiOrg && (
            <Typography variant="caption" noWrap component="div" sx={{ opacity: 0.85, lineHeight: 1.2 }}>
              {orgLabel(org.name, unnamed)}
            </Typography>
          )}
        </Box>
        {end}
        <IconButton
          color="inherit"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={intl.formatMessage({ defaultMessage: "Menu" })}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
          {multiOrg && [
            <MenuItem key="orgs-title" disabled dense>
              <ListItemIcon>
                <SwapHorizIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <FormattedMessage defaultMessage="Organization" />
              </ListItemText>
            </MenuItem>,
            ...org.choices.map((choice) => (
              <MenuItem
                key={`org-${choice.segment}`}
                selected={choice.segment === org.segment}
                onClick={() => {
                  setAnchor(null);
                  navigate(`~/${choice.segment}`);
                }}
              >
                <ListItemIcon>
                  {choice.segment === org.segment && <CheckIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText>{orgLabel(choice.name, unnamed)}</ListItemText>
              </MenuItem>
            )),
            <Divider key="orgs-divider" />,
          ]}
          {locales.map((l) => (
            <MenuItem
              key={`lang-${l}`}
              dense
              selected={l === locale}
              onClick={() => {
                setAnchor(null);
                setLocale(l);
              }}
            >
              <ListItemIcon>{l === locale && <CheckIcon fontSize="small" />}</ListItemIcon>
              <ListItemText>{languageNames[l] ?? l}</ListItemText>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem
            onClick={() => {
              setAnchor(null);
              signout.mutate();
            }}
          >
            <ListItemIcon />
            <ListItemText>
              <FormattedMessage defaultMessage="Log out" />
            </ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
